import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { flightService } from '../../services/flightService';
import bookingService from '../../services/bookingService';
import toast from 'react-hot-toast';
import { navigateWithBookingData, mergeBookingData, serializeBookingData } from '../../utils/bookingQueryParams';
import { useSavedPassengers } from '../../hooks/useSavedPassengers';
import { useSessionExpiration } from '../../hooks/useSessionExpiration';
import { useSessionValidityGate } from '../../hooks/useSessionValidityGate';
import SessionExpiredModal from '../../components/Auth/SessionExpiredModal';
import SessionTimeoutWarningModal from '../../components/Auth/SessionTimeoutWarningModal';
import { getPreservedBookingState, clearPreservedBookingState } from '../../utils/sessionManager';
import { useCurrencyDisplay } from '../../hooks/useCurrencyDisplay';
import {
  calculateFlightPricing,
  calculateFlightFarePerPax,
  calculateFlightLegTotal
} from '../../utils/flightPricing';

const isReturnRoute = (type) =>
  ['Return', 'Returns', 'return', 'round_trip', 'Round Trip'].includes(type || '');

const isMultiCityRoute = (type) => type === 'Multicity';

/** Multicity legs from search only have `price`; API details include seat_price/block_price */
const getMultiCityFlightFarePerPax = (flight) => {
  const fare = calculateFlightFarePerPax(flight);
  if (fare > 0) return fare;
  if (flight?.price != null && !Number.isNaN(Number(flight.price))) {
    return Number(flight.price);
  }
  return 0;
};

const getMultiCityFlightLegTotal = (flight, passengerCount) => {
  const total = calculateFlightLegTotal(flight, passengerCount);
  if (total > 0) return total;
  const farePerPax = getMultiCityFlightFarePerPax(flight);
  if (farePerPax <= 0) return 0;
  return flight?.is_bel_flight ? farePerPax : farePerPax * passengerCount;
};

const resolvePhoneCodeFromCountryId = (countryId, countriesList = []) => {
  if (!countryId || !countriesList.length) return '';
  const match = countriesList.find(
    (country) => String(country.id) === String(countryId)
  );
  return match?.phone_code || '';
};

const resolveCountryIdFromPhoneCode = (phoneCode, countriesList = []) => {
  if (!phoneCode || !countriesList.length) return '';
  const match = countriesList.find(
    (country) => String(country.phone_code) === String(phoneCode)
  );
  return match?.id || '';
};

const getSessionUser = () => {
  try {
    return JSON.parse(sessionStorage.getItem('user') || '{}');
  } catch {
    return {};
  }
};

const AddPassengersPage = ({ onNext, onBack, id: propId, selectedFlights, routeType: propRouteType }) => {
  const { id: paramId } = useParams();
  const id = propId || paramId;
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((state) => state.auth);

  const [flightData, setFlightData] = useState(null);
  const [resolvedSelectedFlights, setResolvedSelectedFlights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [passengers, setPassengers] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [nationalities, setNationalities] = useState([]);
  const [countries, setCountries] = useState([]);
  const [saveForFuture, setSaveForFuture] = useState(false);
  const [bookingOnBehalfOf, setBookingOnBehalfOf] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [hasUserInteracted, setHasUserInteracted] = useState(false); // Track if user has made changes
  const [lockPassengerFields, setLockPassengerFields] = useState(false);

  // Store the userId when component mounts to maintain consistency even if user logs out
  const [initialUserId] = useState(() => {
    try {
      const user = JSON.parse(sessionStorage.getItem('user') || '{}');
      return user.id || 'guest';
    } catch {
      return 'guest';
    }
  });

  // Use saved passengers hook
  const { savedPassengers, loading: loadingSavedPassengers } = useSavedPassengers();

  // Session expiration handling
  const { showSessionModal, handleLoginSuccess, handleCloseModal } = useSessionExpiration();

  const {
    showSessionTimeoutWarning,
    sessionValidity,
    gateProceed,
    handleStaySignedInAndProceed,
    handleLogoutFromWarning
  } = useSessionValidityGate('AddPassengersPage');
  
  // Currency display hook
  const { getFormattedWithBoth } = useCurrencyDisplay();

  // Merge data from URL params, location state, and session storage
  const bookingData = mergeBookingData(location);
  const totalPassengers = bookingData.passengers || 1;
  const routeType = propRouteType || bookingData.routeType || 'One Way';
  const pricing = bookingData.pricing || location.state?.flightData?.pricing || null;
  const isReturn = isReturnRoute(routeType);
  const changeFlightToken = bookingData.booking_token || user?.change_flight || null;
  const isChangeFlight = !!changeFlightToken;

  // Stable key for prop-selected flights (array identity changes every parent render)
  const selectedFlightsKey = selectedFlights?.length
    ? selectedFlights.map((f) => f.id).filter(Boolean).join(',')
    : '';

  // Load original passengers when changing a flight (matches PJO-php behavior)
  useEffect(() => {
    if (!changeFlightToken) return;

    const loadOriginalPassengers = async () => {
      try {
        const response = await bookingService.getBookingByReference(changeFlightToken);
        if (response?.success && response?.data?.passengers?.length) {
          const formatted = response.data.passengers.map((p) => ({
            gender: p.gender || '',
            first_name: p.first_name || '',
            last_name: p.last_name || '',
            email: p.email || '',
            contact_number: p.mobile_number || '',
            country_id: p.country_id || '',
            country_code: resolvePhoneCodeFromCountryId(p.country_id, countries),
            date_of_birth: p.date_of_birth
              ? new Date(p.date_of_birth).toISOString().split('T')[0]
              : '',
            nationality_id: p.nationality_id || ''
          }));
          setPassengers(formatted);
          setLockPassengerFields(true);
        }
      } catch (err) {
        console.error('Failed to load original passengers for change flight:', err);
      }
    };

    loadOriginalPassengers();
  }, [changeFlightToken]);

  // Auto-save passenger data to sessionStorage (more reliable than sessionStorage)
  // Only save if user has interacted with the form
  useEffect(() => {
    if (passengers.length > 0 && hasUserInteracted) {
      // Use sessionStorage instead of sessionStorage for better persistence
      const passengerDataKey = `passenger_form_data_${initialUserId}_${id}`;
      const dataToSave = {
        passengers,
        saveForFuture,
        bookingOnBehalfOf,
        timestamp: Date.now(),
        userId: initialUserId,
        flightId: id
      };
      sessionStorage.setItem(passengerDataKey, JSON.stringify(dataToSave));
      console.log('💾 Auto-saved passenger data to sessionStorage:', {
        key: passengerDataKey,
        userId: initialUserId,
        passengerCount: passengers.length,
        bookingOnBehalfOf,
        data: dataToSave
      });
    }
  }, [passengers, saveForFuture, bookingOnBehalfOf, id, initialUserId, hasUserInteracted]);

  // --- NEW: RECOVERY/REVOCATION LOGIC ---
  useEffect(() => {
    const checkAndRevokeAbandonedLock = async () => {
      const token = sessionStorage.getItem('pending_booking_token');
      const timestamp = sessionStorage.getItem('pending_booking_time');

      if (token) {
        // If it's less than 10 minutes old, it's an active lock we should release
        // since the user is back on the passengers page.
        const timeDiff = Date.now() - parseInt(timestamp || '0');
        const isRecentlyLocked = timeDiff < 10 * 60 * 1000;

        if (isRecentlyLocked) {
          console.log('🧹 [Frontend] Found abandoned booking token on mount, revoking:', token);
          try {
            await bookingService.revokeLock(token);
            console.log('✅ [Frontend] Abandoned seat lock revoked successfully');
          } catch (err) {
            console.warn('⚠️ [Frontend] Failed to revoke abandoned lock:', err);
          }
        }

        // Always clear from sessionStorage once we've tried to revoke (or if old)
        sessionStorage.removeItem('pending_booking_token');
        sessionStorage.removeItem('pending_booking_time');
      }
    };

    checkAndRevokeAbandonedLock();
    
    // Clean up old passenger data from other users on mount (both sessionStorage and sessionStorage)
    try {
      const user = JSON.parse(sessionStorage.getItem('user') || '{}');
      const currentUserId = user.id || 'guest';
      
      if (!currentUserId || currentUserId === 'guest') {
        console.log('⚠️ No valid user ID found, skipping passenger data cleanup');
        return;
      }
      
      const currentUserIdStr = currentUserId.toString();
      const keysToRemove = { sessionStorage: [], sessionStorage: [] };
      
      // Clean sessionStorage
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith('passenger_form_data_')) {
          const withoutPrefix = key.substring('passenger_form_data_'.length);
          const lastUnderscoreIndex = withoutPrefix.lastIndexOf('_');
          
          if (lastUnderscoreIndex > 0) {
            const keyUserId = withoutPrefix.substring(0, lastUnderscoreIndex);
            if (keyUserId !== currentUserIdStr) {
              keysToRemove.sessionStorage.push(key);
            }
          } else {
            keysToRemove.sessionStorage.push(key);
          }
        }
      }
      
      // Clean sessionStorage
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith('passenger_form_data_')) {
          const withoutPrefix = key.substring('passenger_form_data_'.length);
          const lastUnderscoreIndex = withoutPrefix.lastIndexOf('_');
          
          if (lastUnderscoreIndex > 0) {
            const keyUserId = withoutPrefix.substring(0, lastUnderscoreIndex);
            if (keyUserId !== currentUserIdStr) {
              keysToRemove.sessionStorage.push(key);
            }
          } else {
            keysToRemove.sessionStorage.push(key);
          }
        }
      }
      
      // Remove data from other users
      keysToRemove.sessionStorage.forEach(key => {
        sessionStorage.removeItem(key);
        console.log('🧹 Cleaned up passenger data from sessionStorage:', key);
      });
      
      keysToRemove.sessionStorage.forEach(key => {
        sessionStorage.removeItem(key);
        console.log('🧹 Cleaned up passenger data from sessionStorage:', key);
      });
      
      console.log(`✅ Cleanup complete. Current user: ${currentUserId}, Removed ${keysToRemove.sessionStorage.length + keysToRemove.sessionStorage.length} old keys`);
    } catch (error) {
      console.error('❌ Failed to cleanup passenger data:', error);
    }
  }, []);
  // ----------------------------------------

  useEffect(() => {
    const booking = mergeBookingData(location);
    const flightsSource =
      selectedFlights?.length > 0 ? selectedFlights : booking.selectedFlights || [];

    if (flightsSource.length > 0 && isMultiCityRoute(routeType)) {
      const hasApiPricingData = flightsSource.every(
        (f) => f.seat_price != null && f.block_price != null
      );
      if (hasApiPricingData) {
        setResolvedSelectedFlights(flightsSource);
        setFlightData(flightsSource[0]);
        setLoading(false);
      } else {
        fetchMultiCityFlightDetails();
      }
    } else if (isReturn) {
      fetchReturnFlightDetails();
    } else {
      fetchFlightDetails();
    }
    fetchNationalities();
    fetchCountries();
  }, [id, routeType, isReturn, location.search, selectedFlightsKey]);
  
  // Separate effect for initializing passengers - runs only once after mount
  useEffect(() => {
    if (isChangeFlight) return;
    initializePassengers();
  }, []); // Empty dependency array - only run once on mount

  // Resolve dial codes once countries are loaded (profile stores country_id, not +91)
  useEffect(() => {
    if (!countries.length || passengers.length === 0) return;

    setPassengers((prev) => {
      let changed = false;
      const next = prev.map((passenger, index) => {
        const sessionUser = getSessionUser();
        const countryId =
          passenger.country_id ||
          (!bookingOnBehalfOf && index === 0 ? sessionUser.country_id : '') ||
          resolveCountryIdFromPhoneCode(passenger.country_code, countries);
        const countryCode = resolvePhoneCodeFromCountryId(countryId, countries);

        if (
          String(passenger.country_id || '') === String(countryId || '') &&
          String(passenger.country_code || '') === String(countryCode || '')
        ) {
          return passenger;
        }

        changed = true;
        return {
          ...passenger,
          country_id: countryId || passenger.country_id || '',
          country_code: countryCode || passenger.country_code || '',
        };
      });

      return changed ? next : prev;
    });
  }, [countries, bookingOnBehalfOf]);

  const fetchCountries = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api/v1'}/utility/countries`);
      const data = await response.json();
      if (data.success) {
        setCountries(data.data);
      }
    } catch (err) {
      console.error('Failed to fetch countries:', err);
    }
  };

  const fetchNationalities = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000/api/v1'}/utility/nationalities`);
      const data = await response.json();
      if (data.success) {
        setNationalities(data.data);
      }
    } catch (err) {
      console.error('Failed to fetch nationalities:', err);
    }
  };

  const fetchMultiCityFlightDetails = async () => {
    try {
      setLoading(true);
      const source =
        selectedFlights?.length > 0
          ? selectedFlights
          : bookingData.selectedFlights || [];
      const flightIds = source.map((f) => f.id).filter(Boolean);

      if (flightIds.length === 0) {
        setError('No flights selected');
        return;
      }

      const response = await flightService.getMultipleFlightDetails({
        flight_ids: flightIds
      });

      if (response.success && response.data) {
        const flightList = Array.isArray(response.data) ? response.data : [];
        const ordered = flightIds
          .map((fid) => flightList.find((f) => f.id === fid))
          .filter(Boolean);
        const flights = ordered.length > 0 ? ordered : flightList;
        setResolvedSelectedFlights(flights);
        setFlightData(flights[0]);
      } else {
        setError('Failed to load flight details');
      }
    } catch (err) {
      console.error('Failed to fetch multi-city flight details:', err);
      setError('Failed to load flight details');
    } finally {
      setLoading(false);
    }
  };

  const fetchReturnFlightDetails = async () => {
    try {
      setLoading(true);
      const outboundId = parseInt(id, 10);
      const returnId = parseInt(
        bookingData.return_flight_id ||
          selectedFlights?.[1]?.id ||
          bookingData.selectedFlights?.[1]?.id ||
          id,
        10
      );

      const response = await flightService.getMultipleFlightDetails({
        outbound_flight_id: outboundId,
        return_flight_id: returnId
      });

      if (response.success) {
        const outbound = response.data.outbound;
        const returnLeg = response.data.return;
        setResolvedSelectedFlights([outbound, returnLeg]);
        setFlightData(outbound);
      } else {
        setError('Flight not found');
      }
    } catch (err) {
      console.error('Failed to fetch return flight details:', err);
      setError('Failed to load flight details');
    } finally {
      setLoading(false);
    }
  };

  const fetchFlightDetails = async () => {
    try {
      setLoading(true);
      const response = await flightService.getFlightDetails(id, { page_view: 'passenger_page' });

      if (response.success) {
        setFlightData(response.data);
        if (!isReturn) {
          setResolvedSelectedFlights([response.data]);
        }
      } else {
        setError('Flight not found');
      }
    } catch (err) {
      setError('Failed to load flight details');
    } finally {
      setLoading(false);
    }
  };

  const initializePassengers = () => {
    // Priority 0: Check for preserved booking state (from session expiration)
    const preservedState = getPreservedBookingState();
    if (preservedState && preservedState.savedPassengerFormData) {
      console.log('✅ Found preserved passenger form data from session expiration:', preservedState.savedPassengerFormData);
      const saved = preservedState.savedPassengerFormData;
      if (saved.passengers && saved.passengers.length > 0) {
        setPassengers(saved.passengers);
        setSaveForFuture(saved.saveForFuture || false);
        setBookingOnBehalfOf(saved.bookingOnBehalfOf || false);
        setHasUserInteracted(true); // Mark as interacted since we're restoring user data
        // Clear the preserved state after restoring
        clearPreservedBookingState();
        console.log('✅ Restored passenger data from preserved booking state');
        return;
      }
    }
    
    // Priority 1: Check sessionStorage for saved data (more persistent than sessionStorage)
    const user = JSON.parse(sessionStorage.getItem('user') || '{}');
    const userId = user.id || 'guest';
    const passengerDataKey = `passenger_form_data_${userId}_${id}`;
    const savedFormData = sessionStorage.getItem(passengerDataKey);
    
    console.log('🔍 Initializing passengers, checking sessionStorage:', passengerDataKey);
    
    if (savedFormData) {
      try {
        const parsed = JSON.parse(savedFormData);
        console.log('📦 Found saved form data in sessionStorage:', parsed);
        
        // Verify the data belongs to the current user and flight
        const savedUserId = parsed.userId?.toString();
        const currentUserIdStr = userId?.toString();
        
        if (parsed.userId && savedUserId !== currentUserIdStr) {
          console.log('⚠️ Saved data belongs to different user, clearing...');
          sessionStorage.removeItem(passengerDataKey);
        } else if (parsed.flightId && parsed.flightId.toString() !== id.toString()) {
          console.log('⚠️ Saved data belongs to different flight, ignoring...');
        } else {
          // Check if data is recent (within 24 hours for sessionStorage)
          const twentyFourHours = 24 * 60 * 60 * 1000;
          const age = Date.now() - parsed.timestamp;
          console.log('⏰ Saved data age:', Math.floor(age / 1000 / 60), 'minutes');
          
          if (age < twentyFourHours) {
            console.log('✅ Restoring auto-saved passenger data from sessionStorage for user:', userId);
            setPassengers(parsed.passengers);
            setSaveForFuture(parsed.saveForFuture || false);
            setBookingOnBehalfOf(parsed.bookingOnBehalfOf || false);
            setHasUserInteracted(true);
            return;
          } else {
            console.log('⚠️ Auto-saved data expired, clearing...');
            sessionStorage.removeItem(passengerDataKey);
          }
        }
      } catch (error) {
        console.error('❌ Failed to parse saved passenger data:', error);
        sessionStorage.removeItem(passengerDataKey);
      }
    } else {
      console.log('ℹ️ No saved form data found in sessionStorage');
    }
    
    // Priority 2: Check sessionStorage (fallback)
    const sessionKey = `passenger_form_data_${userId}_${id}`;
    const sessionData = sessionStorage.getItem(sessionKey);
    if (sessionData) {
      try {
        const parsed = JSON.parse(sessionData);
        const savedUserId = parsed.userId?.toString();
        const currentUserIdStr = userId?.toString();
        
        if (parsed.userId && savedUserId === currentUserIdStr) {
          const thirtyMinutes = 30 * 60 * 1000;
          const age = Date.now() - parsed.timestamp;
          
          if (age < thirtyMinutes) {
            console.log('✅ Restoring from sessionStorage');
            setPassengers(parsed.passengers);
            setSaveForFuture(parsed.saveForFuture || false);
            setBookingOnBehalfOf(parsed.bookingOnBehalfOf || false);
            setHasUserInteracted(true);
            return;
          }
        }
      } catch (error) {
        console.error('Failed to parse session data:', error);
      }
    }

    // Priority 3: Only use passenger data if we're coming back from payment page (has passengerData in bookingData)
    // Don't use it if we're starting fresh from flight selection
    const hasExistingPassengerData = bookingData.passengerData &&
      Array.isArray(bookingData.passengerData) &&
      bookingData.passengerData.length > 0;

    // Check if this is a fresh booking (coming from flight selection)
    // If there's no passengerData or if the passenger count changed, initialize fresh
    const isFreshBooking = !hasExistingPassengerData ||
      (hasExistingPassengerData && bookingData.passengerData.length !== totalPassengers);

    if (hasExistingPassengerData && !isFreshBooking) {
      // Returning from payment page with existing data
      setPassengers(bookingData.passengerData);
      return;
    }

    // Priority 4: Fresh booking - initialize empty passenger forms
    const initialPassengers = [];
    // Reuse the user variable from above

    for (let i = 0; i < totalPassengers; i++) {
      if (i === 0 && !bookingOnBehalfOf) {
        // First passenger - pre-fill with logged-in user details
        const userCountryId = user.country_id || '';
        initialPassengers.push({
          gender: '',
          first_name: user.first_name || '',
          last_name: user.last_name || '',
          email: user.email || '',
          contact_number: user.mobile_number || '',
          country_id: userCountryId,
          country_code: resolvePhoneCodeFromCountryId(userCountryId, countries),
          date_of_birth: user.date_of_birth || '',
          nationality_id: user.nationality || ''
        });
      } else {
        // Other passengers - empty fields
        initialPassengers.push({
          gender: '',
          first_name: '',
          last_name: '',
          email: i === 0 ? '' : '',
          contact_number: i === 0 ? '' : '',
          country_id: '',
          country_code: '',
          date_of_birth: '',
          nationality_id: ''
        });
      }
    }
    setPassengers(initialPassengers);
  };

  const handlePassengerChange = (index, field, value) => {
    setHasUserInteracted(true); // Mark that user has interacted with the form
    const updatedPassengers = [...passengers];
    updatedPassengers[index][field] = value;

    // Keep country_id and dial code in sync for payment / saved-passenger persistence
    if (field === 'country_code') {
      updatedPassengers[index].country_id = resolveCountryIdFromPhoneCode(value, countries);
    }

    setPassengers(updatedPassengers);
  };

  const handleSelectSavedPassenger = (index, passengerId) => {
    console.log('🔍 handleSelectSavedPassenger called:', { index, passengerId, savedPassengersCount: savedPassengers.length });

    if (!passengerId) {
      console.log('❌ No passengerId provided');
      return;
    }

    // Parse passengerId as it might be a string
    const parsedId = typeof passengerId === 'string' ? parseInt(passengerId) : passengerId;
    console.log('🔍 Looking for passenger with id:', parsedId);
    console.log('🔍 Available saved passengers:', savedPassengers.map(p => ({ id: p.id, type: typeof p.id, name: `${p.first_name} ${p.last_name}` })));

    const savedPassenger = savedPassengers.find(p => {
      // Handle both string and number IDs
      const savedId = typeof p.id === 'string' ? parseInt(p.id) : p.id;
      return savedId === parsedId;
    });

    if (!savedPassenger) {
      console.log('❌ Saved passenger not found');
      return;
    }

    console.log('✅ Found saved passenger:', savedPassenger);

    // Format date of birth to YYYY-MM-DD format for date input
    let formattedDOB = '';
    if (savedPassenger.date_of_birth) {
      const dobDate = new Date(savedPassenger.date_of_birth);
      formattedDOB = dobDate.toISOString().split('T')[0];
    }

    const updatedPassengers = [...passengers];
    updatedPassengers[index] = {
      ...updatedPassengers[index],
      first_name: savedPassenger.first_name || '',
      last_name: savedPassenger.last_name || '',
      email: savedPassenger.email || '',
      contact_number: savedPassenger.mobile_number || '',
      date_of_birth: formattedDOB,
      gender: savedPassenger.gender || '',
      nationality_id: savedPassenger.nationality_id || '',
      country_id: savedPassenger.country_id || '',
      country_code: resolvePhoneCodeFromCountryId(savedPassenger.country_id, countries),
    };

    console.log('📝 Updated passenger data:', updatedPassengers[index]);
    setPassengers(updatedPassengers);
    toast.success('Passenger details loaded');
  };

  const handleGoBack = () => {
    // If inside FlightBookingFlow, call parent onBack to go to previous step
    if (onBack && typeof onBack === 'function') {
      onBack();
      return;
    }

    // Standalone page — navigate back to flight selection
    const flightSelectionPath = routeType === 'Returns' ? '/return-flight' :
      routeType === 'Multicity' ? '/multi-city-search' :
        '/one-way-flight';

    navigateWithBookingData(
      navigate,
      flightSelectionPath,
      {
        ...bookingData,
        passengerData: passengers
      }
    );
  };

  const validateForm = () => {
    console.log('🔍 Validating form...', {
      passengerCount: passengers.length,
      bookingOnBehalfOf
    });

    const newErrors = {};

    for (let i = 0; i < passengers.length; i++) {
      const passenger = passengers[i];
      console.log(`Validating passenger ${i + 1}:`, {
        gender: passenger.gender,
        first_name: passenger.first_name,
        last_name: passenger.last_name,
        email: passenger.email,
        contact_number: passenger.contact_number,
        date_of_birth: passenger.date_of_birth,
        nationality_id: passenger.nationality_id
      });

      if (!passenger.gender) {
        console.log(`❌ Passenger ${i + 1}: Missing gender`);
        newErrors[`gender_${i}`] = 'Please select gender';
      }

      if (!passenger.first_name || passenger.first_name.trim() === '') {
        console.log(`❌ Passenger ${i + 1}: Missing first name`);
        newErrors[`first_name_${i}`] = 'First name is required';
      }

      if (!passenger.last_name || passenger.last_name.trim() === '') {
        console.log(`❌ Passenger ${i + 1}: Missing last name`);
        newErrors[`last_name_${i}`] = 'Last name is required';
      }

      // Only validate email and contact for first passenger when NOT booking on behalf
      if (i === 0 && !bookingOnBehalfOf) {
        if (!passenger.email || passenger.email.trim() === '') {
          console.log(`❌ Passenger ${i + 1}: Missing email`);
          newErrors[`email_${i}`] = 'Email is required';
        }

        if (!passenger.contact_number || passenger.contact_number.trim() === '') {
          console.log(`❌ Passenger ${i + 1}: Missing contact number`);
          newErrors[`contact_number_${i}`] = 'Contact number is required';
        }
      }

      if (!passenger.date_of_birth) {
        console.log(`❌ Passenger ${i + 1}: Missing date of birth`);
        newErrors[`date_of_birth_${i}`] = 'Date of birth is required';
      }

      // Only validate nationality for the first passenger when NOT booking on behalf
      if (i === 0 && !bookingOnBehalfOf) {
        if (!passenger.nationality_id) {
          console.log(`❌ Passenger ${i + 1}: Missing nationality`);
          newErrors[`nationality_id_${i}`] = 'Nationality is required';
        }
      }
    }

    // Check for duplicate passengers (same first name, last name, and date of birth)
    for (let i = 0; i < passengers.length; i++) {
      for (let j = i + 1; j < passengers.length; j++) {
        const passenger1 = passengers[i];
        const passenger2 = passengers[j];

        const sameFirstName = passenger1.first_name.toUpperCase().trim() === passenger2.first_name.toUpperCase().trim();
        const sameLastName = passenger1.last_name.toUpperCase().trim() === passenger2.last_name.toUpperCase().trim();
        const sameDOB = passenger1.date_of_birth === passenger2.date_of_birth;

        if (sameFirstName && sameLastName && sameDOB) {
          console.log(`❌ Duplicate passenger detected: Passenger ${i + 1} and Passenger ${j + 1}`);
          toast.error(`Passenger ${i + 1} and Passenger ${j + 1} have the same name and date of birth. Each passenger must be unique.`);
          setValidationErrors(newErrors);
          return false;
        }
      }
    }

    setValidationErrors(newErrors);
    console.log('✅ All validations passed');
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    console.log('🚀 Form submitted, validating...', {
      passengerCount: passengers.length,
      totalPassengers,
      bookingOnBehalfOf
    });

    if (!validateForm()) {
      console.log('❌ Validation failed');
      return;
    }

    console.log('✅ Validation passed, checking for duplicate bookings...');
    setSubmitting(true);

    try {
      // Prepare flight data for duplicate check
      const flightsToCheck = [];

      const flightsForCheck =
        resolvedSelectedFlights.length > 0 ? resolvedSelectedFlights : selectedFlights;

      if (flightsForCheck && flightsForCheck.length > 0) {
        // Multi-city or return flights
        flightsForCheck.forEach(flight => {
          flightsToCheck.push({
            flight_number: flight.flight_number,
            departure_airport: flight.departure_airport || flight.departure?.airport_id,
            arrival_airport: flight.arrival_airport || flight.arrival?.airport_id,
            departure_date_time: flight.departure_date_time || flight.departure_datetime
          });
        });
      } else if (flightData) {
        // Single flight
        flightsToCheck.push({
          flight_number: flightData.flight_number,
          departure_airport: flightData.departure_airport || flightData.departure?.airport_id,
          arrival_airport: flightData.arrival_airport || flightData.arrival?.airport_id,
          departure_date_time: flightData.departure_date_time || flightData.departure_datetime
        });
      }

      // Prepare passenger data for duplicate check
      const passengersToCheck = passengers.map(p => ({
        first_name: p.first_name.toUpperCase(),
        last_name: p.last_name.toUpperCase(),
        date_of_birth: p.date_of_birth
      }));

      console.log('🔍 Checking duplicates for:', {
        passengers: passengersToCheck,
        flights: flightsToCheck
      });

      // Check for duplicate bookings
      try {
        const duplicateCheckResponse = await bookingService.checkDuplicateBooking(
          passengersToCheck,
          flightsToCheck,
          bookingData.booking_token || changeFlightToken || null  // pass original booking token when rebooking
        );

        if (!duplicateCheckResponse.success) {
          console.log('❌ Duplicate booking detected');
          toast.error('One or more passengers already have a booking for this flight');
          setSubmitting(false);
          return;
        }

        console.log('✅ No duplicate bookings found');
      } catch (duplicateError) {
        // ... (existing duplicate error handling)
        if (duplicateError.response?.status === 409) {
          // Duplicate found
          const duplicates = duplicateError.response?.data?.duplicates || [];
          if (duplicates.length > 0) {
            const duplicateMessages = duplicates.map(d =>
              `${d.passenger} already has a booking for flight ${d.flight} (${d.route})`
            ).join('\n');
            toast.error(`Duplicate booking detected:\n${duplicateMessages}`, { duration: 6000 });
          } else {
            toast.error('One or more passengers already have a booking for this flight');
          }
          setSubmitting(false);
          return;
        } else {
          console.warn('⚠️ Duplicate check failed, continuing anyway:', duplicateError);
        }
      }

      // --- DEFERRED: SEAT LOCKING moved to PaymentPage ---
      const bookingToken = changeFlightToken;
      // ---------------------------------------------------

      // Prepare booking data with passenger info
      const updatedBookingData = {
        ...bookingData,
        passengerData: passengers,
        passengers: totalPassengers,
        routeType,
        farePerPax: pricingData.farePerPax,
        totalFare: pricingData.totalFare,
        blockFare: pricingData.blockFare,
        amount: pricingData.isSeatPricing ? pricingData.totalFare : pricingData.blockFare,
        saveForFuture,
        booking_token: bookingToken, // Add the lock token
        // Pass selectedFlights if available (for return/multi-city)
        selectedFlights:
          resolvedSelectedFlights.length > 0
            ? resolvedSelectedFlights
            : bookingData.selectedFlights || undefined
      };

      const passengerPayload = {
        passengers,
        totalPassengers,
        routeType,
        pricing: pricingData,
        saveForFuture,
        booking_token: bookingToken
      };

      await gateProceed(async () => {
        if (onNext && typeof onNext === 'function') {
          console.log('📞 Calling onNext callback (inside FlightBookingFlow)...');
          await onNext(passengerPayload);
          return;
        }

        const paymentPath = `/payment/${id}`;
        const queryString = serializeBookingData(updatedBookingData);
        const fullUrl = queryString ? `${paymentPath}?${queryString}` : paymentPath;

        navigate(fullUrl, {
          replace: false,
          state: {
            flightData,
            totalPassengers,
            routeType,
            pricing: pricingData,
            saveForFuture,
            booking_token: bookingToken,
            selectedFlights:
              resolvedSelectedFlights.length > 0
                ? resolvedSelectedFlights
                : bookingData.selectedFlights || undefined
          }
        });

        console.log('✅ Navigation initiated to:', fullUrl);
      });
    } catch (err) {
      console.error('❌ Form submission error:', err);
      toast.error('Failed to proceed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const calculatePricing = () => {
    if (pricing) {
      return pricing;
    }

    const flights =
      resolvedSelectedFlights.length > 0
        ? resolvedSelectedFlights
        : flightData
          ? [flightData]
          : [];

    if (flights.length === 0) {
      return { farePerPax: 0, totalFare: 0, blockFare: 0, isSeatPricing: false };
    }

    if (flights.length >= 2) {
      const fareFn = isMultiCityRoute(routeType)
        ? getMultiCityFlightFarePerPax
        : calculateFlightFarePerPax;
      const legTotalFn = isMultiCityRoute(routeType)
        ? getMultiCityFlightLegTotal
        : calculateFlightLegTotal;
      const farePerPax = flights.reduce((sum, leg) => sum + fareFn(leg), 0);
      const totalFare = flights.reduce(
        (sum, leg) => sum + legTotalFn(leg, totalPassengers),
        0
      );
      return {
        farePerPax,
        totalFare,
        blockFare: 0,
        isSeatPricing: true
      };
    }

    return calculateFlightPricing(flights[0], totalPassengers);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <img src="/assets/images/flight_logo.gif" alt="Loading..." className="w-24 h-24" />
      </div>
    );
  }

  if (error || !flightData) {
    return (
      <div className="min-h-screen pt-[70px]">
        <div className="container mx-auto px-4 py-8">
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error || 'Flight not found'}
          </div>
        </div>
      </div>
    );
  }

  const pricingData = calculatePricing();

  return (
    <div className="min-h-screen pt-[70px]">
      {/* Session Expired Modal */}
      <SessionExpiredModal
        isOpen={showSessionModal}
        onClose={handleCloseModal}
        onLoginSuccess={handleLoginSuccess}
      />

      <SessionTimeoutWarningModal
        isOpen={showSessionTimeoutWarning}
        remainingTime={sessionValidity}
        onStaySignedIn={handleStaySignedInAndProceed}
        onLogout={handleLogoutFromWarning}
        continueMessage={'To continue your booking securely, we need to refresh your session. Click "Stay Signed In" to continue, or you\'ll be logged out.'}
        successToastMessage="Session extended. You can continue with your booking."
      />

      {/* Main Content */}
      <section className="py-10">
        <div className="container mx-auto px-4">
          <div className="bg-white rounded shadow-sm">
            <div className="p-6">
              <div className="flex flex-wrap -mx-4">
                {/* Left Column - Passenger Forms */}
                <div className="w-full lg:w-2/3 px-4">
                  {/* Header Card */}
                  <div className="bg-white border-0 rounded-none mb-4">
                    <div className="bg-[#ffd200] px-4 py-3">
                      <h5 className="text-base font-bold m-0">Passenger(s) Information</h5>
                    </div>
                  </div>

                  <form onSubmit={handleSubmit} className="px-3">
                    {passengers.map((passenger, index) => (
                      <div key={index} className="mb-6">
                        <h5 className="text-base font-bold mb-3">Passenger {index + 1}</h5>

                        {/* Checkbox - Only for first passenger */}
                        {index === 0 && !lockPassengerFields && (
                          <div className="mb-4 flex items-center">
                            <input
                              type="checkbox"
                              id="booking_on_behalf"
                              checked={bookingOnBehalfOf}
                              onChange={(e) => {
                                const isChecked = e.target.checked;
                                setBookingOnBehalfOf(isChecked);

                                // When checking "booking on behalf", clear email/contact/nationality for first passenger
                                // When unchecking, restore user's details
                                if (passengers.length > 0) {
                                  const updatedPassengers = [...passengers];
                                  const user = JSON.parse(sessionStorage.getItem('user') || '{}');

                                  if (isChecked) {
                                    // Clear email, contact, and nationality for first passenger
                                    updatedPassengers[0] = {
                                      ...updatedPassengers[0],
                                      email: '',
                                      contact_number: '',
                                      country_id: '',
                                      country_code: '',
                                      nationality_id: ''
                                    };
                                  } else {
                                    // Restore user's details for first passenger
                                    const userCountryId = user.country_id || '';
                                    updatedPassengers[0] = {
                                      ...updatedPassengers[0],
                                      email: user.email || '',
                                      contact_number: user.mobile_number || '',
                                      country_id: userCountryId,
                                      country_code: resolvePhoneCodeFromCountryId(userCountryId, countries),
                                      nationality_id: user.nationality || ''
                                    };
                                  }

                                  setPassengers(updatedPassengers);
                                }
                                // Don't set hasUserInteracted here - wait for actual field input
                              }}
                              className="mr-2 w-4 h-4 cursor-pointer"
                            />
                            <label htmlFor="booking_on_behalf" className="text-sm cursor-pointer flex items-center">
                              Are you booking on behalf of someone else?
                            </label>
                          </div>
                        )}

                        {/* Saved Passengers Dropdown */}
                        {!lockPassengerFields && savedPassengers.length > 0 && (
                          <>
                            {/* For first passenger: only show when booking on behalf */}
                            {index === 0 && bookingOnBehalfOf && (
                              <div className="mb-4">
                                <select
                                  className="w-full md:w-1/2 px-3 py-2 text-base border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                  onChange={(e) => handleSelectSavedPassenger(index, e.target.value)}
                                  defaultValue=""
                                >
                                  <option value="">Select a passenger</option>
                                  {savedPassengers.map((saved) => (
                                    <option key={saved.id} value={saved.id}>
                                      {saved.first_name} {saved.last_name}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            )}
                            {/* For other passengers: always show */}
                            {index > 0 && (
                              <div className="mb-4">
                                <select
                                  className="w-full md:w-1/2 px-3 py-2 text-base border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                  onChange={(e) => handleSelectSavedPassenger(index, e.target.value)}
                                  defaultValue=""
                                >
                                  <option value="">Select a passenger</option>
                                  {savedPassengers.map((saved) => (
                                    <option key={saved.id} value={saved.id}>
                                      {saved.first_name} {saved.last_name}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            )}
                          </>
                        )}

                        {/* Gender Selection */}
                        <div className="w-full md:w-1/2 px-0 pr-3 mb-3">
                          <select
                            name={`gender_${index}`}
                            value={passenger.gender}
                            onChange={(e) => handlePassengerChange(index, 'gender', e.target.value)}
                            disabled={lockPassengerFields}
                            className={`w-full px-3 py-2 text-base border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 ${lockPassengerFields ? 'bg-white cursor-default pointer-events-none' : ''}`}
                          >
                            <option value="">Select Gender</option>
                            <option value="Mister">Male</option>
                            <option value="Miss">Female</option>
                          </select>
                          {validationErrors[`gender_${index}`] && (
                            <p className="text-red-500 text-sm mt-1">{validationErrors[`gender_${index}`]}</p>
                          )}
                        </div>

                        {/* Name Fields */}
                        <div className="flex flex-wrap -mx-2 mb-3">
                          <div className="w-full md:w-1/2 px-2 mb-3 md:mb-0">
                            <input
                              type="text"
                              name={`first_name_${index}`}
                              placeholder="FIRST NAME"
                              value={passenger.first_name}
                              readOnly={lockPassengerFields}
                              onChange={(e) => handlePassengerChange(index, 'first_name', e.target.value.toUpperCase())}
                              className={`w-full px-3 py-2 text-base border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 uppercase placeholder-gray-400 ${lockPassengerFields ? 'bg-white cursor-default' : ''}`}
                              style={{ textTransform: 'uppercase' }}
                            />
                            {validationErrors[`first_name_${index}`] && (
                              <p className="text-red-500 text-sm mt-1">{validationErrors[`first_name_${index}`]}</p>
                            )}
                          </div>
                          <div className="w-full md:w-1/2 px-2">
                            <input
                              type="text"
                              name={`last_name_${index}`}
                              placeholder="LAST NAME"
                              value={passenger.last_name}
                              readOnly={lockPassengerFields}
                              onChange={(e) => handlePassengerChange(index, 'last_name', e.target.value.toUpperCase())}
                              className={`w-full px-3 py-2 text-base border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 uppercase placeholder-gray-400 ${lockPassengerFields ? 'bg-white cursor-default' : ''}`}
                              style={{ textTransform: 'uppercase' }}
                            />
                            {validationErrors[`last_name_${index}`] && (
                              <p className="text-red-500 text-sm mt-1">{validationErrors[`last_name_${index}`]}</p>
                            )}
                          </div>
                        </div>

                        {/* Conditional Fields - Show only if NOT booking on behalf OR if not first passenger */}
                        {!lockPassengerFields && (!bookingOnBehalfOf || index !== 0) && (
                          <>
                            {/* Email and Contact for first passenger */}
                            {index === 0 && (
                              <div className="flex flex-wrap -mx-2 mb-3">
                                <div className="w-full md:w-1/2 px-2 mb-3 md:mb-0">
                                  <input
                                    type="email"
                                    name={`email_${index}`}
                                    placeholder="USER@JETSCANNER360.COM"
                                    value={passenger.email}
                                    onChange={(e) => handlePassengerChange(index, 'email', e.target.value.toUpperCase())}
                                    className="w-full px-3 py-2 text-base border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 uppercase placeholder-gray-400"
                                    style={{ textTransform: 'uppercase' }}
                                  />
                                  {validationErrors[`email_${index}`] && (
                                    <p className="text-red-500 text-sm mt-1">{validationErrors[`email_${index}`]}</p>
                                  )}
                                </div>
                                <div className="w-full md:w-1/2 px-2">
                                  <div className="flex gap-2">
                                    <select
                                      name={`country_code_${index}`}
                                      value={passenger.country_code || ''}
                                      onChange={(e) => handlePassengerChange(index, 'country_code', e.target.value)}
                                      className="w-32 px-3 py-2 text-base border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                    >
                                      <option value="">Select code</option>
                                      {countries.map((country) => (
                                        <option key={country.id} value={country.phone_code}>
                                          {country.name} {country.phone_code}
                                        </option>
                                      ))}
                                    </select>
                                    <input
                                      type="tel"
                                      name={`contact_number_${index}`}
                                      placeholder="CONTACT NUMBER"
                                      value={passenger.contact_number}
                                      onChange={(e) => handlePassengerChange(index, 'contact_number', e.target.value)}
                                      className="flex-1 px-3 py-2 text-base border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 uppercase placeholder-gray-400"
                                    />
                                  </div>
                                  {validationErrors[`contact_number_${index}`] && (
                                    <p className="text-red-500 text-sm mt-1">{validationErrors[`contact_number_${index}`]}</p>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Date of Birth and Nationality - Only Nationality for Passenger 1 */}
                            <div className="flex flex-wrap -mx-2 mb-3">
                              <div className="w-full md:w-1/2 px-2 mb-3 md:mb-0">
                                <input
                                  type="text"
                                  name={`date_of_birth_${index}`}
                                  placeholder="DATE OF BIRTH"
                                  value={passenger.date_of_birth}
                                  onFocus={(e) => {
                                    e.target.type = 'date';
                                    try { e.target.showPicker(); } catch (err) {}
                                  }}
                                  onClick={(e) => {
                                    try { e.target.showPicker(); } catch (err) {}
                                  }}
                                  onBlur={(e) => {
                                    if (!e.target.value) e.target.type = 'text';
                                  }}
                                  max={new Date().toISOString().split('T')[0]}
                                  onChange={(e) => handlePassengerChange(index, 'date_of_birth', e.target.value)}
                                  className="w-full px-3 py-2 text-base border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 uppercase placeholder-gray-400 [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-moz-calendar-picker-indicator]:hidden"
                                />
                                {validationErrors[`date_of_birth_${index}`] && (
                                  <p className="text-red-500 text-sm mt-1">{validationErrors[`date_of_birth_${index}`]}</p>
                                )}
                              </div>
                              {index === 0 && (
                                <div className="w-full md:w-1/2 px-2">
                                  <select
                                    name={`nationality_id_${index}`}
                                    value={passenger.nationality_id}
                                    onChange={(e) => handlePassengerChange(index, 'nationality_id', e.target.value)}
                                    className="w-full px-3 py-2 text-base border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                  >
                                    <option value="">Nationality</option>
                                    {nationalities.map((nationality) => (
                                      <option key={nationality.id} value={nationality.id}>
                                        {nationality.nationality}
                                      </option>
                                    ))}
                                  </select>
                                  {validationErrors[`nationality_id_${index}`] && (
                                    <p className="text-red-500 text-sm mt-1">{validationErrors[`nationality_id_${index}`]}</p>
                                  )}
                                </div>
                              )}
                            </div>
                          </>
                        )}

                        {/* Simplified Fields - Show only if booking on behalf AND first passenger */}
                        {!lockPassengerFields && bookingOnBehalfOf && index === 0 && (
                          <>
                            {/* Date of Birth - Full Width */}
                            <div className="w-full md:w-1/2 px-0 pr-3 mb-3">
                              <input
                                type="text"
                                name={`date_of_birth_${index}`}
                                placeholder="DATE OF BIRTH"
                                value={passenger.date_of_birth}
                                onFocus={(e) => {
                                  e.target.type = 'date';
                                  try { e.target.showPicker(); } catch (err) {}
                                }}
                                onClick={(e) => {
                                  try { e.target.showPicker(); } catch (err) {}
                                }}
                                onBlur={(e) => {
                                  if (!e.target.value) e.target.type = 'text';
                                }}
                                max={new Date().toISOString().split('T')[0]}
                                onChange={(e) => handlePassengerChange(index, 'date_of_birth', e.target.value)}
                                className="w-full px-3 py-2 text-base border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 uppercase placeholder-gray-400 [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-moz-calendar-picker-indicator]:hidden"
                              />
                              {validationErrors[`date_of_birth_${index}`] && (
                                <p className="text-red-500 text-sm mt-1">{validationErrors[`date_of_birth_${index}`]}</p>
                              )}
                            </div>
                          </>
                        )}

                        {/* Date of birth for change-flight (readonly) */}
                        {lockPassengerFields && (
                          <div className="w-full md:w-1/2 px-0 pr-3 mb-3">
                            <input
                              type="text"
                              name={`date_of_birth_${index}`}
                              placeholder="DATE OF BIRTH"
                              value={passenger.date_of_birth}
                              readOnly
                              className="w-full px-3 py-2 text-base border border-gray-300 rounded bg-white cursor-default uppercase placeholder-gray-400"
                            />
                          </div>
                        )}

                        {/* Save for Future Bookings - Only for last passenger */}
                        {index === passengers.length - 1 && !lockPassengerFields && (
                          <div className="mb-3">
                            <label className="flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={saveForFuture}
                                onChange={(e) => setSaveForFuture(e.target.checked)}
                                className="mr-2"
                              />
                              <span className="text-sm">Save for Future Bookings</span>
                            </label>
                          </div>
                        )}
                      </div>
                    ))}

                    <div className="mt-4 mb-2 mr-2 flex justify-end gap-2">
                      <button
                        type="button"
                        className="px-4 py-2 bg-gray-300 text-black text-sm font-normal rounded shadow-none hover:bg-gray-400 transition-colors"
                        onClick={handleGoBack}
                      >
                        Back
                      </button>
                      <button
                        type="submit"
                        className="px-4 py-2 bg-[#ffd200] text-black text-sm font-normal rounded shadow-none hover:bg-[#e6bb00] transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                        disabled={submitting}
                      >
                        {submitting ? 'Processing...' : 'Next'}
                      </button>
                    </div>
                  </form>
                </div>

                {/* Right Column - Fare Summary */}
                <div className="w-full lg:w-1/3 px-4 mt-6 lg:mt-0">
                  <div className="bg-white border-0 rounded-none overflow-hidden sticky top-24">
                    <div className="bg-[#ffd200] px-4 py-3">
                      <h5 className="text-base font-bold m-0">Fare</h5>
                    </div>
                    <div className="p-4">
                      {/* Multi-city fare breakdown */}
                      {isMultiCityRoute(routeType) && resolvedSelectedFlights.length >= 2 ? (
                        <div className="space-y-4">
                          {resolvedSelectedFlights.map((flight, idx) => {
                            const farePax = getMultiCityFlightFarePerPax(flight);
                            return (
                              <div key={idx}>
                                <div className="flex justify-between items-center mb-2">
                                  <span className="text-sm font-bold text-gray-700">
                                    {flight.departure?.airport_code || 'DEP'}
                                  </span>
                                  <span className="text-gray-400 text-xs">→</span>
                                  <span className="text-sm font-bold text-gray-700">
                                    {flight.arrival?.airport_code || 'ARR'}
                                  </span>
                                </div>
                                <div className="space-y-1 text-xs">
                                  <div className="flex justify-between">
                                    <span className="text-gray-500">Passenger(s)</span>
                                    <span className="text-gray-700 font-semibold">{totalPassengers}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-gray-500">Fare/Pax</span>
                                    <span className="text-gray-700 font-semibold">{getFormattedWithBoth(farePax)}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-gray-500">Total Fare</span>
                                    <span className="text-gray-700 font-semibold">
                                      {getFormattedWithBoth(
                                        getMultiCityFlightLegTotal(flight, totalPassengers)
                                      )}
                                    </span>
                                  </div>
                                </div>
                                {idx < resolvedSelectedFlights.length - 1 && (
                                  <div className="border-t-2 border-dashed border-gray-300 mt-3"></div>
                                )}
                              </div>
                            );
                          })}
                          {/* Grand Total */}
                          <div className="border-t-2 border-dashed border-gray-300"></div>
                          <div className="space-y-1 text-xs">
                            <div className="flex justify-between">
                              <span className="text-gray-700 font-bold">Total Passenger(s)</span>
                              <span className="text-gray-700 font-bold">{totalPassengers}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-700 font-bold">Grand Total</span>
                              <span className="text-gray-700 font-bold">
                                {getFormattedWithBoth(
                                  resolvedSelectedFlights.reduce(
                                    (sum, flight) =>
                                      sum + getMultiCityFlightLegTotal(flight, totalPassengers),
                                    0
                                  )
                                )}
                              </span>
                            </div>
                          </div>
                        </div>
                      ) : resolvedSelectedFlights.length === 2 && !isMultiCityRoute(routeType) ? (
                        // Return flight fare breakdown
                        <div className="space-y-4">
                          {/* Outbound Flight */}
                          <div>
                            <div className="flex justify-between items-center mb-3">
                              <span className="text-sm font-bold text-gray-700">
                                {resolvedSelectedFlights[0].departure?.airport_code || 'DEP'}
                              </span>
                              <span className="text-gray-400">→</span>
                              <span className="text-sm font-bold text-gray-700">
                                {resolvedSelectedFlights[0].arrival?.airport_code || 'ARR'}
                              </span>
                            </div>
                            <div className="space-y-1 text-xs">
                              <div className="flex justify-between">
                                <span className="text-gray-500">Passenger(s)</span>
                                <span className="text-gray-700 font-semibold">{totalPassengers}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-500">Fare/Pax</span>
                                <span className="text-gray-700 font-semibold">
                                  {getFormattedWithBoth(calculateFlightFarePerPax(resolvedSelectedFlights[0]))}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-500">Total Fare</span>
                                <span className="text-gray-700 font-semibold">
                                  {getFormattedWithBoth(
                                    calculateFlightLegTotal(resolvedSelectedFlights[0], totalPassengers)
                                  )}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Divider */}
                          <div className="border-t-2 border-dashed border-gray-300"></div>

                          {/* Return Flight */}
                          <div>
                            <div className="flex justify-between items-center mb-3">
                              <span className="text-sm font-bold text-gray-700">
                                {resolvedSelectedFlights[1].departure?.airport_code || 'DEP'}
                              </span>
                              <span className="text-gray-400">→</span>
                              <span className="text-sm font-bold text-gray-700">
                                {resolvedSelectedFlights[1].arrival?.airport_code || 'ARR'}
                              </span>
                            </div>
                            <div className="space-y-1 text-xs">
                              <div className="flex justify-between">
                                <span className="text-gray-500">Passenger(s)</span>
                                <span className="text-gray-700 font-semibold">{totalPassengers}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-500">Fare/Pax</span>
                                <span className="text-gray-700 font-semibold">
                                  {getFormattedWithBoth(calculateFlightFarePerPax(resolvedSelectedFlights[1]))}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-500">Total Fare</span>
                                <span className="text-gray-700 font-semibold">
                                  {getFormattedWithBoth(
                                    calculateFlightLegTotal(resolvedSelectedFlights[1], totalPassengers)
                                  )}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Divider */}
                          <div className="border-t-2 border-dashed border-gray-300"></div>

                          {/* Grand Total */}
                          <div className="space-y-1 text-xs">
                            <div className="flex justify-between">
                              <span className="text-gray-700 font-bold">Total Passenger(s)</span>
                              <span className="text-gray-700 font-bold">{totalPassengers}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-700 font-bold">Grand Total</span>
                              <span className="text-gray-700 font-bold">
                                {getFormattedWithBoth(
                                  resolvedSelectedFlights.reduce(
                                    (sum, leg) => sum + calculateFlightLegTotal(leg, totalPassengers),
                                    0
                                  )
                                )}
                              </span>
                            </div>
                          </div>
                        </div>
                      ) : (
                        // One-way flight fare breakdown (original)
                        <div className="space-y-3">
                          <div className="flex justify-between items-center">
                            <p className="text-sm mb-0">
                              {flightData?.departure?.airport_code || 'LCB'}
                            </p>
                            <b className="text-sm">
                              {flightData?.arrival?.airport_code || 'TAB'}
                            </b>
                          </div>
                          <div className="flex justify-between items-center">
                            <p className="text-sm mb-0">Passengers</p>
                            <b className="text-sm">{totalPassengers}</b>
                          </div>
                          <div className="flex justify-between items-center">
                            <p className="text-sm mb-0">Fare/Pax</p>
                            <b className="text-sm">
                              {getFormattedWithBoth(pricingData.farePerPax || pricingData.blockFare)}
                            </b>
                          </div>
                          <div className="flex justify-between items-center pt-3 border-t border-gray-200">
                            <p className="text-sm mb-0 font-bold">Total Fare</p>
                            <b className="text-sm">
                              {getFormattedWithBoth(pricingData.isSeatPricing ? pricingData.totalFare : pricingData.blockFare)}
                            </b>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AddPassengersPage;

